import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { KEYS, storage } from '@/lib/storage';
import { Camera, CheckCircle, BookOpen } from 'lucide-react';

const GRID_PHOTOS = [
  { key: 'wedding_dance', alt: 'First dance at a wedding' },
  { key: 'kids_birthday', alt: 'Kids blowing out birthday candles' },
  { key: 'party_crowd', alt: 'Friends celebrating at a party' },
  { key: 'graduation', alt: 'Graduation cap toss' },
  { key: 'family_picnic', alt: 'Family picnic outdoors' },
  { key: 'sparkler_sendoff', alt: 'Sparkler send-off at night' },
];

const STEPS = [
  { n: 1, Icon: Camera, label: 'Guests capture', testId: 'landing-step-1' },
  { n: 2, Icon: CheckCircle, label: 'Host curates', testId: 'landing-step-2' },
  { n: 3, Icon: BookOpen, label: 'Everyone relives', testId: 'landing-step-3' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { status, user } = useAuth();

  useEffect(() => {
    if (status !== 'signed-in' || !user) return;
    const token = storage.get(KEYS.pendingJoinToken);
    if (token) {
      storage.remove(KEYS.pendingJoinToken);
      navigate(`/event-preview?token=${encodeURIComponent(token)}`, { replace: true });
      return;
    }
    const code = storage.get(KEYS.pendingJoinCode);
    if (code) {
      storage.remove(KEYS.pendingJoinCode);
      navigate(`/event-preview?event_id_code=${encodeURIComponent(code)}`, { replace: true });
      return;
    }
    const acct = (user.account_type || 'ATTENDEE').toUpperCase();
    if (user.role === 'HOST' || user.role === 'SUPER_ADMIN' || acct === 'HOST' || acct === 'PROFESSIONAL') {
      navigate('/host-dashboard', { replace: true });
    } else {
      navigate('/contrib-event', { replace: true });
    }
  }, [status, user, navigate]);

  if (status === 'loading' || status === 'signed-in') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="loading-fallback">
        <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="landing-signed-out">
      <div className="flex-1 flex flex-col items-center px-5 pt-12 sm:pt-16 pb-12 max-w-3xl mx-auto w-full">

        {/* 1 — Brand V2 lockup (unchanged) */}
        <img
          src="/allviews-lockup-portrait.svg"
          alt="ALLVIEWS"
          className="w-56 sm:w-64 mb-5"
          data-testid="landing-logo"
        />

        {/* 2 — Single tagline */}
        <h1
          className="text-xl sm:text-2xl font-extrabold text-[#0B1B46] text-center leading-snug tracking-wide mb-8"
          data-testid="landing-tagline"
        >
          Relive every moment<br />through the people who were there.
        </h1>

        {/* 3 — Photo grid */}
        <div
          className="w-full grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10"
          data-testid="landing-photo-grid"
        >
          {GRID_PHOTOS.map((p, i) => (
            <div
              key={p.key}
              className="grid-enter-card relative aspect-[4/3] rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow"
              style={{ animationDelay: `${120 + i * 90}ms` }}
              data-testid={`landing-photo-${p.key}`}
            >
              <img
                src={`/images/lander/${p.key}.jpg`}
                alt={p.alt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* 4 — How it works */}
        <p
          className="text-[11px] font-extrabold text-[#5B6472] tracking-[1.2px] uppercase mb-5"
          data-testid="landing-how-it-works-heading"
        >
          How it works
        </p>
        <div className="flex justify-center gap-8 sm:gap-14 mb-10 w-full max-w-md">
          {STEPS.map((s) => (
            <div key={s.n} className="flex flex-col items-center gap-1.5" data-testid={s.testId}>
              <div className="w-[52px] h-[52px] rounded-full bg-[#146BFF] flex items-center justify-center shadow-[0_4px_12px_rgba(20,107,255,0.35)]">
                <s.Icon className="w-[22px] h-[22px] text-white" strokeWidth={2} />
              </div>
              <span className="text-xs font-extrabold text-[#146BFF] mt-1">{s.n}</span>
              <span className="text-xs font-semibold text-[#0B1B46] text-center leading-4">{s.label}</span>
            </div>
          ))}
        </div>

        {/* 5 — CTAs */}
        <div className="w-full max-w-sm flex flex-col gap-2.5">
          <Link
            to="/sign-in"
            className="w-full flex items-center justify-center h-11 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg transition-colors no-underline focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="landing-sign-in-btn"
          >
            Sign In
          </Link>
          <Link
            to="/register"
            className="w-full flex items-center justify-center h-11 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors no-underline focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="landing-register-btn"
          >
            Create Account
          </Link>
          <Link
            to="/join"
            className="w-full flex items-center justify-center h-11 text-[#146BFF] font-bold rounded-lg hover:bg-[#EFF5FF] transition-colors no-underline underline focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="landing-join-btn"
          >
            Have an invite? Join an Event
          </Link>
        </div>
      </div>
    </div>
  );
}
